/**
 * Controls (docs/spec/07 §3): rebind the six actions (two keys each), reset, touch controls, and
 * the fixed keys and pad layout for reference.
 */

import type { Action, Bindings } from '../../platform/options';
import { ACTIONS, DEFAULT_BINDINGS, FIXED_FIRE } from '../../platform/options';
import type { Screen } from '../../render/screen';
import type { KeyInput, MenuAction, PointerInput } from '../scene';
import { UiScene } from '../kit/base';
import { drawPanel } from '../kit/chrome';
import type { Rect } from '../kit/menuModel';
import { inRect, wrapIndex } from '../kit/menuModel';
import { drawOptionRow } from '../kit/optionRow';
import { drawSpinner } from '../kit/spinner';
import { ACCENT, BAD, blink, FAINT, GOOD, INK, MUTED, SELECTED } from '../kit/theme';
import { ACTION_LABELS, bindKey, canBind, clearKey, unbound } from './controlsModel';

const ROW0 = 34;
const ROW_H = 11;
const SLOT_X = [128, 218];
const SLOT_W = 84;
/** Rows 0-5 are the actions; then RESET and TOUCH. */
const RESET_ROW = ACTIONS.length;
const TOUCH_ROW = RESET_ROW + 1;
const ROWS = TOUCH_ROW + 1;
const TOUCH_VALUES = ['auto', 'on', 'off'] as const;

function rowY(row: number): number {
  return ROW0 + row * ROW_H + (row >= RESET_ROW ? 4 : 0);
}

function slotRect(row: number, slot: number): Rect {
  return { x: SLOT_X[slot] ?? 128, y: rowY(row) - 2, w: SLOT_W, h: 11 };
}

export class ControlsScene extends UiScene {
  protected readonly heading = 'CONTROLS';
  protected override readonly track = null;
  private row = 0;
  private slot: 0 | 1 = 0;
  private waiting = false;
  private flash = '';
  private flashAt = -9999;

  private get action_(): Action | undefined {
    return ACTIONS[this.row];
  }

  protected override describe(): string {
    return `Controls. ${this.rowText()}`;
  }

  private rowText(): string {
    const a = this.action_;
    const keys = this.services.options.get().keys;
    if (a) return `${ACTION_LABELS[a]}, key ${this.slot + 1}: ${this.services.input.keyName(keys[a][this.slot])}`;
    if (this.row === RESET_ROW) return 'Reset to defaults';
    return `Touch controls: ${this.services.options.get().touch}`;
  }

  protected override hint(): string {
    if (this.waiting) return 'PRESS A KEY   ESC CANCELS   DEL CLEARS';
    return `${this.words().ok} REBIND   DEL CLEAR   ${this.words().back} BACK`;
  }

  protected paint(screen: Screen): void {
    const o = this.services.options.get();
    const name = (code: string): string => this.services.input.keyName(code);
    screen.text('ACTION', 22, 22, FAINT);
    screen.text('KEY 1', SLOT_X[0] ?? 0, 22, FAINT);
    screen.text('KEY 2', SLOT_X[1] ?? 0, 22, FAINT);
    ACTIONS.forEach((a, row) => {
      const y = rowY(row);
      const on = row === this.row;
      if (on) drawSpinner(screen, 8, y, this.ms);
      screen.text(ACTION_LABELS[a], 22, y, on ? SELECTED : INK);
      o.keys[a].forEach((code, slot) => {
        const r = slotRect(row, slot);
        const sel = on && slot === this.slot;
        const wait = sel && this.waiting;
        screen.box(r.x, r.y, r.w, r.h, sel ? SELECTED : FAINT);
        const label = wait ? (blink(this.ms, 600) ? 'PRESS KEY' : '') : name(code);
        screen.text(label, r.x + r.w / 2, r.y + 2, wait ? SELECTED : code ? ACCENT : FAINT, 'center');
      });
    });
    const resetOn = this.row === RESET_ROW;
    if (resetOn) drawSpinner(screen, 8, rowY(RESET_ROW), this.ms);
    screen.text('RESET TO DEFAULTS', 22, rowY(RESET_ROW), resetOn ? SELECTED : INK);
    drawOptionRow(screen, rowY(TOUCH_ROW), 'TOUCH CONTROLS', { kind: 'choice', text: o.touch.toUpperCase() },
      this.row === TOUCH_ROW, this.ms, { label: 22, value: 162 });
    this.paintReference(screen, o.keys);
  }

  private paintReference(screen: Screen, keys: Bindings): void {
    const box = { x: 8, y: 130, w: 304, h: 55 };
    drawPanel(screen, box);
    const fixed = [...new Set(FIXED_FIRE.map((c) => this.services.input.keyName(c).replace(/^R\./, '')))].join(' AND ');
    screen.text(`${fixed} ALWAYS GRAB, TOO.`, 16, 136, MUTED);
    const pad = this.services.input.gamepad();
    screen.text(`PAD${pad ? ' ✓' : ':'} STICK, A = GRAB, START = PAUSE`, 16, 147, pad ? GOOD : MUTED);
    screen.text('TOUCH: PAD BOTTOM LEFT, GRAB RIGHT', 16, 158, MUTED);
    const missing = unbound(keys);
    const recent = this.ms - this.flashAt < 1600;
    if (recent) screen.text(this.flash, 160, 172, ACCENT, 'center');
    else if (missing[0]) screen.text(`NO KEY FOR ${ACTION_LABELS[missing[0]]}!`, 160, 172, BAD, 'center');
  }

  private note(text: string): void {
    this.flash = text;
    this.flashAt = this.ms;
    this.say(text);
  }

  private move(row: number, slot: 0 | 1 = this.slot): void {
    this.row = wrapIndex(row, ROWS);
    this.slot = slot;
    this.sound('move');
    this.say(this.rowText());
  }

  private activate(): void {
    const a = this.action_;
    if (a) {
      this.waiting = true;
      this.sound('select');
      this.say(`Press a key for ${ACTION_LABELS[a]}`);
    } else if (this.row === RESET_ROW) {
      this.services.options.update({ keys: DEFAULT_BINDINGS });
      this.sound('select');
      this.note('KEYS RESET TO DEFAULTS');
    } else this.cycleTouch(1);
  }

  private cycleTouch(delta: number): void {
    const o = this.services.options.get();
    const i = TOUCH_VALUES.indexOf(o.touch);
    this.services.options.update({ touch: TOUCH_VALUES[wrapIndex(i + delta, TOUCH_VALUES.length)] ?? 'auto' });
    this.sound('move');
    this.say(this.rowText());
  }

  keydown(k: KeyInput): boolean {
    const a = this.action_;
    if (!this.waiting) {
      if (a && (k.code === 'Delete' || (k.code === 'Backspace' && k.shift))) {
        this.services.options.update({ keys: clearKey(this.services.options.get().keys, a, this.slot) });
        this.sound('back');
        return true;
      }
      return false;
    }
    if (!a) {
      this.waiting = false;
      return false;
    }
    if (k.code === 'Escape' && a !== 'pause') {
      this.waiting = false;
      this.sound('back');
      return true;
    }
    if (k.code === 'Delete' || k.code === 'Backspace') {
      this.services.options.update({ keys: clearKey(this.services.options.get().keys, a, this.slot) });
      this.waiting = false;
      this.sound('back');
      return true;
    }
    if (!canBind(a, k.code)) {
      this.sound('error');
      return true;
    }
    this.services.options.update({ keys: bindKey(this.services.options.get().keys, a, this.slot, k.code) });
    this.waiting = false;
    this.sound('select');
    this.note(`${ACTION_LABELS[a]} = ${this.services.input.keyName(k.code)}`);
    return true;
  }

  protected override back(): void {
    if (this.waiting) {
      this.waiting = false;
      this.sound('back');
    } else super.back();
  }

  protected onAction(a: MenuAction): void {
    if (this.waiting) return;
    if (a === 'up') this.move(this.row - 1);
    else if (a === 'down') this.move(this.row + 1);
    else if (a === 'left' || a === 'right') {
      if (this.action_) this.move(this.row, a === 'left' ? 0 : 1);
      else if (this.row === TOUCH_ROW) this.cycleTouch(a === 'left' ? -1 : 1);
    } else if (a === 'confirm') this.activate();
  }

  protected override onPointer(p: PointerInput): void {
    if (this.waiting || (p.kind !== 'move' && p.kind !== 'down') || (p.kind === 'down' && p.button !== 0)) return;
    for (let row = 0; row < ROWS; row++) {
      const y = rowY(row);
      if (p.y < y - 2 || p.y >= y + ROW_H - 2 || p.x < 4 || p.x > 312) continue;
      const slot: 0 | 1 = row < RESET_ROW && p.x >= (SLOT_X[1] ?? 218) - 3 ? 1 : 0;
      if (row !== this.row || slot !== this.slot) this.move(row, slot);
      if (p.kind === 'down' && (row >= RESET_ROW || inRect(slotRect(row, slot), p.x, p.y))) this.activate();
      return;
    }
  }
}
